import { Apollo } from 'src/gql/generated';

/**
 * The skill as the editor shows it. The author edits the draft, so its instructions, files
 * and completeness replace the published ones. Everyone else sees the published skill.
 */
export function getEditorSkill(
  skill: Apollo.AgentSkillSimpleFragment
): Apollo.AgentSkillSimpleFragment {
  if (!skill.draft) return skill;
  return {
    ...skill,
    attachments: skill.draft.attachments,
    content: skill.draft.content,
    description: skill.draft.description,
    isComplete: skill.draft.isComplete,
    name: skill.draft.name,
  };
}

/**
 * What the editor loaded: the draft revision for the author, otherwise the published
 * version. A change seen through the cache with another key was made elsewhere.
 */
export function getSkillEditorKey(
  skill: Pick<Apollo.AgentSkillSimpleFragment, 'currentVersion' | 'draft'>
): string | null {
  if (skill.draft) return `draft:${skill.draft.revision}`;
  return skill.currentVersion ? `version:${skill.currentVersion.id}` : null;
}

/**
 * The editor's skill with file processing states taken from `latest`, the same draft
 * revision as seen through the cache. Processing writes into the draft without bumping its
 * revision, so the editor keeps its own copy and takes only these fields. Returns `current`
 * itself when nothing changed.
 */
export function getSkillWithLatestFileStates({
  current,
  latest,
}: {
  current: Apollo.AgentSkillSimpleFragment;
  latest: Apollo.AgentSkillSimpleFragment;
}): Apollo.AgentSkillSimpleFragment {
  if (!current.draft || !latest.draft || current.draft.revision !== latest.draft.revision)
    return current;
  const latestByPath = new Map(latest.draft.attachments.map(file => [file.path, file]));
  const getIsStale = (file: Apollo.AgentSkillAttachmentFieldsFragment) => {
    const next = latestByPath.get(file.path);
    return (
      next !== undefined &&
      (next.processing !== file.processing || next.processingError !== file.processingError)
    );
  };
  if (!current.attachments.some(getIsStale) && current.draft.filesReady === latest.draft.filesReady)
    return current;
  const merge = (files: Apollo.AgentSkillAttachmentFieldsFragment[]) =>
    files.map(file => {
      const next = latestByPath.get(file.path);
      return next
        ? { ...file, processing: next.processing, processingError: next.processingError }
        : file;
    });
  return {
    ...current,
    attachments: merge(current.attachments),
    draft: {
      ...current.draft,
      attachments: merge(current.draft.attachments),
      filesReady: latest.draft.filesReady,
    },
  };
}

/** Why the draft's files keep it from being tested or published, or '' when they don't. */
export function getDraftFilesHint(
  attachments: Pick<Apollo.AgentSkillAttachmentFieldsFragment, 'processing'>[]
): string {
  if (attachments.some(file => file.processing === Apollo.AgentSkillFileProcessing.Failed))
    return 'A file could not be read. Retry or remove it first.';
  if (attachments.some(file => file.processing === Apollo.AgentSkillFileProcessing.Processing))
    return 'Files are still processing.';
  return '';
}

/** Why Publish is unavailable, or '' when the draft can be published. */
export function getPublishBlocker({
  filesHint,
  runHint,
  skill,
}: {
  filesHint: string;
  /** Why the instructions are not ready, from the editor's own checks. */
  runHint: string;
  skill: Pick<Apollo.AgentSkillSimpleFragment, 'hasUnpublishedChanges' | 'isPublished'>;
}): string {
  if (skill.isPublished && !skill.hasUnpublishedChanges) return 'No unpublished changes.';
  if (runHint) return runHint.replace(/ to run\.$/, ' to publish.');
  return filesHint;
}
